/**
 * @description
 * React Error Boundary for authentication errors.
 * Catches and displays user-friendly error messages for auth-related failures.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date October 7, 2025
 * @see admin/src/types/auth.ts
 */

import React, { Component, ReactNode } from 'react';
import { AuthError } from '../../../types/auth';
import { getErrorTitle } from './authErrorUtils';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary for authentication errors
 */
export class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to monitoring service in production
    if (import.meta.env.MODE === 'production') {
      // TODO: Send to error monitoring service (e.g., Sentry)
      console.error('Auth Error Boundary caught error:', {
        error: error.message,
        type: error instanceof AuthError ? error.type : 'unknown',
        componentStack: errorInfo.componentStack,
      });
    } else {
      console.error('Auth Error:', error, errorInfo);
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }

      return <DefaultAuthErrorFallback error={this.state.error} reset={this.reset} />;
    }

    return this.props.children;
  }
}

/**
 * Default error fallback UI
 */
export function DefaultAuthErrorFallback({ error, reset }: { error: Error; reset: () => void }) {
  const isAuthError = error instanceof AuthError;
  const errorMessage = isAuthError
    ? error.message
    : 'An unexpected error occurred. Please try again.';

  const errorType = isAuthError ? error.type : 'unknown';

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6 rounded-xl border border-destructive bg-card p-8 shadow-lg">
        {/* Error Icon */}
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
          <svg
            className="h-8 w-8 text-destructive"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        {/* Error Title */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground">
            {getErrorTitle(errorType)}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">{errorMessage}</p>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={reset}
            className="w-full rounded-lg bg-primary px-4 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Try Again
          </button>

          <button
            onClick={() => (window.location.href = '/')}
            className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm font-medium text-foreground hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Return to Home
          </button>
        </div>

        {/* Help Text */}
        <div className="border-t pt-4 text-center text-xs text-muted-foreground">
          <p>
            If this problem persists, please{' '}
            <a href="/support" className="underline hover:text-foreground">
              contact support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}


/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */